import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { transferSwiftGetFxQuoteQueryKey } from '@column/api';

import { IntlWireRepository, type IntlWireTransfer } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import type { FxQuote } from '~/typings/API';

export type ReviewQuoteTransfer = Pick<
  IntlWireTransfer,
  'id' | 'amount' | 'currencyCode' | 'fxQuoteId' | 'instructedAmount' | 'instructedCurrencyCode'
>;

export const getInternationalWireReviewQuoteTerms = (transfer?: ReviewQuoteTransfer) => {
  const useInstructedAmount = Boolean(transfer?.instructedCurrencyCode && transfer.instructedCurrencyCode !== 'USD');
  return {
    buyAmount: useInstructedAmount ? transfer?.instructedAmount : transfer?.amount,
    buyCurrencyCode: useInstructedAmount ? transfer?.instructedCurrencyCode : transfer?.currencyCode,
  };
};

export const isFXQuoteExpired = (quote: FxQuote, now = Date.now()) => {
  const expiresAt = new Date(quote.expiredAt).getTime();
  return !Number.isFinite(expiresAt) || expiresAt <= now;
};

export const isFXQuoteUsableForReview = (
  quote: FxQuote,
  buyAmount?: number,
  buyCurrencyCode?: string,
  now = Date.now()
) =>
  buyAmount !== undefined &&
  quote.buyAmount === buyAmount &&
  quote.buyCurrencyCode === buyCurrencyCode &&
  (quote.status === 'not_booked' || quote.status === 'booked') &&
  !isFXQuoteExpired(quote, now);

export const useInternationalWireReviewQuote = (transfer?: ReviewQuoteTransfer, isActive = true) => {
  const addDangerNotification = useNotificationStore((state) => state.addDangerNotification);
  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const handleFXQuoteError = useCallback(
    (error: { message?: string }) =>
      addDangerNotification({ content: error.message ?? 'An error occurred while creating an FX quote.' }),
    [addDangerNotification]
  );
  const { buyAmount, buyCurrencyCode } = getInternationalWireReviewQuoteTerms(transfer);
  const fxQuoteId = transfer?.fxQuoteId;
  const transferID = transfer?.id;
  const isFXTransfer = buyCurrencyCode !== undefined && buyCurrencyCode !== 'USD';
  const canRequestQuote = buyAmount !== undefined && Boolean(buyCurrencyCode);
  const existingQuoteQuery = useQuery({
    queryKey: transferSwiftGetFxQuoteQueryKey({ path: { fx_quote_id: fxQuoteId ?? '' } }),
    queryFn: () => IntlWireRepository.getFxQuote({ id: fxQuoteId! }),
    enabled: Boolean(isActive && isFXTransfer && canRequestQuote && platformId && fxQuoteId),
    retry: false,
    staleTime: Infinity,
  });
  const {
    data: replacementQuoteData,
    isError: replacementQuoteError,
    isPending: isCreatingQuote,
    mutateAsync: createFXQuote,
    variables: replacementQuoteVariables,
  } = useMutation({
    mutationFn: ({
      buyAmount: requestBuyAmount,
      buyCurrencyCode: requestBuyCurrencyCode,
    }: {
      buyAmount: number;
      buyCurrencyCode: string;
      transferID?: string;
    }) => IntlWireRepository.createFxQuote({ buyAmount: requestBuyAmount, buyCurrencyCode: requestBuyCurrencyCode }),
    onError: handleFXQuoteError,
  });
  const replacementMatchesTransfer =
    replacementQuoteVariables?.transferID === transferID &&
    replacementQuoteVariables?.buyAmount === buyAmount &&
    replacementQuoteVariables?.buyCurrencyCode === buyCurrencyCode;
  const replacementQuote =
    replacementMatchesTransfer &&
    replacementQuoteData &&
    isFXQuoteUsableForReview(replacementQuoteData, buyAmount, buyCurrencyCode)
      ? replacementQuoteData
      : undefined;
  const existingQuote = existingQuoteQuery.data;
  const existingQuoteIsUsable = existingQuote
    ? isFXQuoteUsableForReview(existingQuote, buyAmount, buyCurrencyCode)
    : false;
  const shouldRequestReplacement =
    !fxQuoteId || existingQuoteQuery.isError || Boolean(existingQuote && !existingQuoteIsUsable);
  const hasReplacementResult =
    replacementMatchesTransfer && (Boolean(replacementQuoteData) || isCreatingQuote || replacementQuoteError);
  const requestQuote = useCallback(async () => {
    if (buyAmount === undefined || !buyCurrencyCode || !isFXTransfer) return;
    try {
      await createFXQuote({
        buyAmount,
        buyCurrencyCode,
        transferID,
      });
    } catch {
      return;
    }
  }, [buyAmount, buyCurrencyCode, createFXQuote, isFXTransfer, transferID]);

  useEffect(() => {
    if (!isActive || !isFXTransfer || !canRequestQuote || !shouldRequestReplacement || hasReplacementResult) return;
    void requestQuote();
  }, [canRequestQuote, hasReplacementResult, isActive, isFXTransfer, requestQuote, shouldRequestReplacement]);

  const quote = replacementQuote ?? (existingQuoteIsUsable ? existingQuote : undefined);
  useEffect(() => {
    if (!quote?.expiredAt || !isActive) return;
    const delay = Math.max(1000, new Date(quote.expiredAt).getTime() - Date.now() + 100);
    const timeout = window.setTimeout(() => void requestQuote(), delay);
    return () => window.clearTimeout(timeout);
  }, [isActive, quote?.expiredAt, requestQuote]);

  const isWaitingToRequestReplacement =
    isActive && isFXTransfer && canRequestQuote && shouldRequestReplacement && !hasReplacementResult;

  return {
    isFXTransfer,
    isLoading: existingQuoteQuery.isLoading || isCreatingQuote || isWaitingToRequestReplacement,
    quote,
    refreshQuote: requestQuote,
  };
};
